import { useState, useEffect } from "react";
import { AlertTriangle, Merge, X, Check, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import { findPotentialDuplicates, mergeFunds } from "@/lib/fund-matching";
import { supabase } from "@/integrations/supabase/client";

interface DuplicatePair {
  fund1: { id: string; name: string };
  fund2: { id: string; name: string };
  similarity: number;
  sameIssuer: boolean;
}

interface FundDetails {
  id: string;
  name: string;
  issuer: string | null;
  vintage_year: number | null;
  sec_cik: string | null;
  lei: string | null;
  positionCount: number;
  familyNames: string[];
  totalNav: number;
}

interface DuplicateResolverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved?: () => void;
}

export function DuplicateResolver({
  open,
  onOpenChange,
  onResolved,
}: DuplicateResolverProps) {
  const { orgId } = useTenant();
  const { toast } = useToast();

  const [duplicates, setDuplicates] = useState<DuplicatePair[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMerging, setIsMerging] = useState(false);
  const [fund1Details, setFund1Details] = useState<FundDetails | null>(null);
  const [fund2Details, setFund2Details] = useState<FundDetails | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string>("");
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && orgId) {
      loadDuplicates();
    }
  }, [open, orgId]);

  useEffect(() => {
    if (duplicates.length > 0 && currentIndex < duplicates.length) {
      loadFundDetails(duplicates[currentIndex]);
    }
  }, [duplicates, currentIndex]);

  const loadDuplicates = async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const pairs = await findPotentialDuplicates(orgId, 0.7);
      // Filter out dismissed pairs
      const filtered = pairs.filter(
        (p) => !dismissed.has(`${p.fund1.id}-${p.fund2.id}`)
      );
      setDuplicates(filtered);
      setCurrentIndex(0);
    } catch (error) {
      console.error("Failed to load duplicates:", error);
      toast({
        title: "Error",
        description: "Failed to load potential duplicates",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadFundDetails = async (pair: DuplicatePair) => {
    if (!orgId) return;

    try {
      // Load fund 1 details
      const { data: fund1 } = await supabase
        .from("instruments")
        .select("id, name, issuer, vintage_year, sec_cik, lei")
        .eq("id", pair.fund1.id)
        .single();

      // Load fund 2 details
      const { data: fund2 } = await supabase
        .from("instruments")
        .select("id, name, issuer, vintage_year, sec_cik, lei")
        .eq("id", pair.fund2.id)
        .single();

      // Load position counts and NAV for fund 1
      const { data: positions1 } = await supabase
        .from("positions")
        .select(
          `
          id,
          family:families(name),
          position_metrics(nav_base, period_end)
        `
        )
        .eq("instrument_id", pair.fund1.id);

      // Load position counts and NAV for fund 2
      const { data: positions2 } = await supabase
        .from("positions")
        .select(
          `
          id,
          family:families(name),
          position_metrics(nav_base, period_end)
        `
        )
        .eq("instrument_id", pair.fund2.id);

      const getFundDetails = (
        fund: typeof fund1,
        positions: typeof positions1
      ): FundDetails | null => {
        if (!fund) return null;

        const familyNames = [
          ...new Set(
            (positions || [])
              .map((p) => (p.family as { name: string } | null)?.name)
              .filter(Boolean) as string[]
          ),
        ];

        // Get latest NAV for each position
        let totalNav = 0;
        for (const pos of positions || []) {
          const metrics = pos.position_metrics as
            | Array<{ nav_base: number | null; period_end: string }>
            | null;
          if (metrics && metrics.length > 0) {
            const sorted = [...metrics].sort(
              (a, b) =>
                new Date(b.period_end).getTime() -
                new Date(a.period_end).getTime()
            );
            totalNav += sorted[0]?.nav_base || 0;
          }
        }

        return {
          id: fund.id,
          name: fund.name,
          issuer: fund.issuer,
          vintage_year: fund.vintage_year,
          sec_cik: fund.sec_cik,
          lei: fund.lei,
          positionCount: positions?.length || 0,
          familyNames,
          totalNav,
        };
      };

      setFund1Details(getFundDetails(fund1, positions1));
      setFund2Details(getFundDetails(fund2, positions2));

      // Default to the fund with more positions or higher NAV as target
      const f1Details = getFundDetails(fund1, positions1);
      const f2Details = getFundDetails(fund2, positions2);
      if (f1Details && f2Details) {
        if (
          f1Details.positionCount > f2Details.positionCount ||
          f1Details.totalNav > f2Details.totalNav
        ) {
          setSelectedTarget(pair.fund1.id);
        } else {
          setSelectedTarget(pair.fund2.id);
        }
      }
    } catch (error) {
      console.error("Failed to load fund details:", error);
    }
  };

  const handleMerge = async () => {
    if (!orgId || !selectedTarget) return;

    const currentPair = duplicates[currentIndex];
    const sourceId =
      selectedTarget === currentPair.fund1.id
        ? currentPair.fund2.id
        : currentPair.fund1.id;

    setIsMerging(true);
    try {
      const result = await mergeFunds(orgId, selectedTarget, sourceId);

      if (result.success) {
        toast({
          title: "Funds merged",
          description: "The duplicate funds have been merged successfully",
        });

        // Move to next pair
        if (currentIndex < duplicates.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          // Reload to check for any remaining duplicates
          await loadDuplicates();
        }

        onResolved?.();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Merge failed:", error);
      toast({
        title: "Merge failed",
        description:
          error instanceof Error ? error.message : "Failed to merge funds",
        variant: "destructive",
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleDismiss = () => {
    const currentPair = duplicates[currentIndex];
    setDismissed((prev) =>
      new Set(prev).add(`${currentPair.fund1.id}-${currentPair.fund2.id}`)
    );

    if (currentIndex < duplicates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Remove dismissed pair and check if any remain
      const remaining = duplicates.filter(
        (_, i) =>
          i !== currentIndex &&
          !dismissed.has(`${duplicates[i].fund1.id}-${duplicates[i].fund2.id}`)
      );
      setDuplicates(remaining);
      setCurrentIndex(0);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const currentPair = duplicates[currentIndex];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Potential Duplicate Funds
          </DialogTitle>
          <DialogDescription>
            {isLoading
              ? "Scanning for duplicates..."
              : duplicates.length === 0
              ? "No potential duplicates found"
              : `Found ${duplicates.length} potential duplicate${
                  duplicates.length > 1 ? "s" : ""
                } (${currentIndex + 1}/${duplicates.length})`}
          </DialogDescription>
        </DialogHeader>

        {!isLoading && duplicates.length > 0 && currentPair && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Badge variant="secondary">
                {Math.round(currentPair.similarity * 100)}% similar
              </Badge>
              {currentPair.sameIssuer && (
                <Badge variant="outline">Same GP</Badge>
              )}
            </div>

            <RadioGroup
              value={selectedTarget}
              onValueChange={setSelectedTarget}
              className="grid grid-cols-2 gap-4"
            >
              {fund1Details && (
                <Label htmlFor={fund1Details.id} className="cursor-pointer">
                  <Card
                    className={`${
                      selectedTarget === fund1Details.id
                        ? "border-primary ring-2 ring-primary"
                        : ""
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">
                          <Building2 className="h-4 w-4 inline mr-2" />
                          {fund1Details.name}
                        </CardTitle>
                        <RadioGroupItem
                          value={fund1Details.id}
                          id={fund1Details.id}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {fund1Details.issuer && (
                        <p className="text-muted-foreground">
                          GP: {fund1Details.issuer}
                        </p>
                      )}
                      {fund1Details.vintage_year && (
                        <p className="text-muted-foreground">
                          Vintage: {fund1Details.vintage_year}
                        </p>
                      )}
                      <div className="pt-2 border-t space-y-1">
                        <p>
                          <span className="font-medium">
                            {fund1Details.positionCount}
                          </span>{" "}
                          position{fund1Details.positionCount !== 1 ? "s" : ""}
                        </p>
                        <p>
                          NAV:{" "}
                          <span className="font-medium">
                            {formatCurrency(fund1Details.totalNav)}
                          </span>
                        </p>
                        {fund1Details.familyNames.length > 0 && (
                          <p className="text-muted-foreground">
                            Families: {fund1Details.familyNames.join(", ")}
                          </p>
                        )}
                      </div>
                      {(fund1Details.sec_cik || fund1Details.lei) && (
                        <div className="pt-2 border-t">
                          {fund1Details.sec_cik && (
                            <Badge variant="outline" className="mr-1">
                              CIK: {fund1Details.sec_cik}
                            </Badge>
                          )}
                          {fund1Details.lei && (
                            <Badge variant="outline">
                              LEI: {fund1Details.lei.substring(0, 8)}...
                            </Badge>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Label>
              )}

              {fund2Details && (
                <Label htmlFor={fund2Details.id} className="cursor-pointer">
                  <Card
                    className={`${
                      selectedTarget === fund2Details.id
                        ? "border-primary ring-2 ring-primary"
                        : ""
                    }`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">
                          <Building2 className="h-4 w-4 inline mr-2" />
                          {fund2Details.name}
                        </CardTitle>
                        <RadioGroupItem
                          value={fund2Details.id}
                          id={fund2Details.id}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {fund2Details.issuer && (
                        <p className="text-muted-foreground">
                          GP: {fund2Details.issuer}
                        </p>
                      )}
                      {fund2Details.vintage_year && (
                        <p className="text-muted-foreground">
                          Vintage: {fund2Details.vintage_year}
                        </p>
                      )}
                      <div className="pt-2 border-t space-y-1">
                        <p>
                          <span className="font-medium">
                            {fund2Details.positionCount}
                          </span>{" "}
                          position{fund2Details.positionCount !== 1 ? "s" : ""}
                        </p>
                        <p>
                          NAV:{" "}
                          <span className="font-medium">
                            {formatCurrency(fund2Details.totalNav)}
                          </span>
                        </p>
                        {fund2Details.familyNames.length > 0 && (
                          <p className="text-muted-foreground">
                            Families: {fund2Details.familyNames.join(", ")}
                          </p>
                        )}
                      </div>
                      {(fund2Details.sec_cik || fund2Details.lei) && (
                        <div className="pt-2 border-t">
                          {fund2Details.sec_cik && (
                            <Badge variant="outline" className="mr-1">
                              CIK: {fund2Details.sec_cik}
                            </Badge>
                          )}
                          {fund2Details.lei && (
                            <Badge variant="outline">
                              LEI: {fund2Details.lei.substring(0, 8)}...
                            </Badge>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Label>
              )}
            </RadioGroup>

            <p className="text-sm text-center text-muted-foreground">
              Select the fund to keep. All positions from the other fund will be
              merged into it.
            </p>
          </div>
        )}

        {!isLoading && duplicates.length === 0 && (
          <div className="text-center py-8">
            <Check className="h-12 w-12 mx-auto text-green-500 mb-3" />
            <p className="font-medium">No duplicates found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Your fund catalog is clean!
            </p>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {duplicates.length > 0 && (
            <>
              <Button
                variant="ghost"
                onClick={handleDismiss}
                disabled={isMerging}
              >
                <X className="h-4 w-4" />
                Keep Separate
              </Button>
              <Button
                onClick={handleMerge}
                disabled={isMerging || !selectedTarget}
              >
                <Merge className="h-4 w-4" />
                {isMerging ? "Merging..." : "Merge Funds"}
              </Button>
            </>
          )}
          {duplicates.length === 0 && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
